import type { Router } from 'vue-router'
import { usePermissionStore } from '@/stores/permission'
import type { PermissionedRouteCandidate } from '@/config/businessRoutePermissions'
import type { PermissionRequirement } from '@/types/permission'
import { clearAuthClientState, hasMustChangePasswordFlag } from '@/utils/authState'
import { getFirstAccessibleRoute } from '@/utils/defaultRoute'

export function setupRouterGuards(router: Router) {
  router.beforeEach((to, _from, next) => {
    const publicPages = ['/login', '/register', '/forgot-password', '/sso/callback', '/403']
    const authRequired = !publicPages.includes(to.path)
    const user = localStorage.getItem('user')
    const token = localStorage.getItem('token')
    const isAuthenticated = Boolean(user && token)

    // 已登录用户访问登录页，自动跳转首页
    if (to.path === '/login' && isAuthenticated) {
      next('/')
      return
    }

    if (authRequired && !isAuthenticated) {
      clearAuthClientState()
      next({ path: '/login', query: { redirect: to.fullPath } })
      return
    }

    if (isAuthenticated) {
      try {
        const parsedUser = JSON.parse(user || '{}')
        if (hasMustChangePasswordFlag(parsedUser) && to.path !== '/force-change-password') {
          next('/force-change-password')
          return
        }
      } catch {
        // ignore malformed local user state; normal auth checks handle it
      }
    }

    // 权限路由检查（如果路由配置了 meta.permission）
    const requiredPermission = to.meta?.permission as PermissionRequirement | undefined
    if (requiredPermission && isAuthenticated) {
      const permStore = usePermissionStore()
      permStore.loadPermissions()

      if (!permStore.canAccess(requiredPermission)) {
        const defaultRoute = getMatchedDefaultRoute(to, permStore.permissions)
        if (defaultRoute && defaultRoute !== to.fullPath) {
          next(defaultRoute)
          return
        }
        next('/403')
        return
      }
    }

    next()
  })
}

function getMatchedDefaultRoute(to: any, permissions: string[]): string | undefined {
  const matchedWithDefault = [...(to.matched || [])].reverse().find((record: any) => {
    return record.meta?.defaultRouteBasePath === to.path
      && Array.isArray(record.meta?.defaultRouteCandidates)
  })

  if (!matchedWithDefault) return undefined

  return getFirstAccessibleRoute(
    matchedWithDefault.meta.defaultRouteCandidates as PermissionedRouteCandidate[],
    permissions
  )
}
